import api from '@/api';

import { PageResultStock } from '@/core/request/PageResultStock';

import {
  Quotation,
  CreateQuotationRequest,
  UpdateQuotationRequest,
  QuotationItem,
  CreateQuotationItemRequest,
  UpdateQuotationItemRequest,
  QuotationSupplier,
  CreateQuotationSupplierRequest,
  SendQuotationRequest,
  ReportQuotationItemsSuppliersRequest,
  QuotationsQuery,
  QuotationGetResponse,
  ReportQuotationItemsSuppliersResponse,
  ReportQuotationItemsByQuotationResponse,
  ReportQuotationSupplierWinnersResponse,
} from './Quotations.types';

import { API_BASE_URL } from '..';

const CONTROLLER_URL = `${API_BASE_URL}/quotations`;

export async function listQuotations(params?: QuotationsQuery) {
  const result = (await api.get(`${CONTROLLER_URL}`, params)) as unknown;

  return result as PageResultStock<Quotation>;
}

export async function getQuotation(id: number) {
  const result = (await api.get(`${CONTROLLER_URL}/${id}`, null)) as unknown;

  return result as QuotationGetResponse;
}

export async function createQuotation(obj: CreateQuotationRequest) {
  const result = (await api.post(`${CONTROLLER_URL}`, obj, null)) as unknown;

  return result as QuotationGetResponse;
}

export async function updateQuotation(obj: UpdateQuotationRequest) {
  const result = (await api.put(
    `${CONTROLLER_URL}/${obj.id}`,
    obj,
    null,
  )) as unknown;

  return result;
}

export async function deleteQuotation(id: number) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${id}`,
    {},
    null,
  )) as unknown;

  return result;
}

export async function sendQuotation(
  quotationId: number,
  obj: SendQuotationRequest,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${quotationId}/send`,
    obj,
    null,
  )) as unknown;

  return result;
}

/** Quotation Items */

export async function listQuotationItems(
  quotationId: number,
): Promise<PageResultStock<QuotationItem>> {
  const result = (await api.get(
    `${CONTROLLER_URL}/${quotationId}/items`,
    null,
  )) as unknown;

  return result as PageResultStock<QuotationItem>;
}

export async function createQuotationItem(
  quotationId: number,
  obj: CreateQuotationItemRequest,
) {
  const result = (await api.post(
    `${CONTROLLER_URL}/${quotationId}/items`,
    obj,
    null,
  )) as unknown;

  return result as number[];
}

export async function updateQuotationItem(
  quotationId: number,
  obj: UpdateQuotationItemRequest,
): Promise<QuotationItem> {
  const result = (await api.put(
    `${CONTROLLER_URL}/${quotationId}/items/${obj.id}`,
    obj,
    null,
  )) as unknown;

  return result as QuotationItem;
}

export async function deleteQuotationItem(quotationId: number, id: number) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${quotationId}/items/${id}`,
    {},
    null,
  )) as unknown;

  return result;
}

/** Quotation Suppliers */

export async function listQuotationSuppliers(
  quotationId: number,
  offer?: boolean,
): Promise<PageResultStock<QuotationSupplier>> {
  const result = (await api.get(`${CONTROLLER_URL}/${quotationId}/suppliers`, {
    offer: offer ?? false,
  })) as unknown;

  return result as PageResultStock<QuotationSupplier>;
}

export async function createQuotationSupplier(
  quotationId: number,
  obj: CreateQuotationSupplierRequest,
): Promise<QuotationSupplier> {
  const result = (await api.post(
    `${CONTROLLER_URL}/${quotationId}/suppliers`,
    obj,
    null,
  )) as unknown;

  return result as QuotationSupplier;
}

export async function deleteQuotationSupplier(quotationId: number, id: number) {
  const result = (await api.remove(
    `${CONTROLLER_URL}/${quotationId}/suppliers/${id}`,
    {},
    null,
  )) as unknown;

  return result;
}

export async function reportQuotationItemsBySyppliers({
  quotationId,
  ...params
}: ReportQuotationItemsSuppliersRequest) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${quotationId}/reports/items-by-suppliers`,
    params,
  )) as unknown;

  return result as ReportQuotationItemsSuppliersResponse;
}

export async function reportQuotationItemsByQuotation(quotationId?: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${quotationId}/reports/items-by-quotations`,
    null,
  )) as unknown;

  return result as ReportQuotationItemsByQuotationResponse;
}

export async function reportQuotationSupplierWinners(quotationId?: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${quotationId}/reports/suppliers-winners`,
    null,
  )) as unknown;

  return result as ReportQuotationSupplierWinnersResponse;
}
